import type { RxCollection, RxDocumentData } from 'rxdb';
import { entityConfigs, valuesEqual } from '@ast-data/shared';
import type { AppDatabase } from '../../db/database';
import { authFetch } from '../../utils/authFetch';
import {
    forgetPending,
    pendingEntries,
    subscribeAuthorJournal,
    type PendingEntry
} from '../../db/authorJournal';
import { uploadOrphans } from '../support/salvage';
import {
    isReplicatingHere,
    replicationStates,
    subscribeReplicationPresence
} from '../../db/replication';
import { getSyncBlockers } from '../../db/syncBlockers';
import {
    forgetRejections,
    rejectionFor,
    type PushRejection
} from './pushRejections';

/**
 * What "N ausstehend" is actually made of — and whether it is still true.
 *
 * The count comes from the author journal (see db/authorJournal): one entry per
 * locally written document, dropped when a push accepts it. The two halves can
 * come apart, and then the number stands still for ever:
 *
 * - the local database was wiped and rebuilt under the journal, which is kept
 *   through a wipe on purpose (it may carry unsent field work), so its entries
 *   outlive the documents;
 * - the server refused the write and sent its own state back — a push conflict,
 *   and ALSO what an unauthorized write looks like (see backend
 *   `replicated.push`). The conflict handler resolves it through the replication
 *   protocol, which fires no collection hook, so the entry stays. This is the
 *   one that adds up quietly, one per refused write;
 * - a pull wrote the server's state over the local document before the push got
 *   to it, so RxDB considers it synced and never sends it;
 * - the browser refused to store the journal at all, freezing the count.
 *
 * The queue then holds a name with nothing behind it: no error, no blocker, no
 * push, and neither a reload, a sign-out nor "Daten neu laden" can help.
 *
 * This module pairs every entry back up with its document, and PROVES which
 * entries are stale before dropping them. `startQueueSelfHeal` runs that same
 * proof by itself in the window that syncs; the dialog's button is the same
 * check, asked for by hand.
 */

export type PendingDocState =
    /** Still on the device, waiting its turn. */
    | 'waiting'
    /** Deleted here; the deletion itself is what has to go up. */
    | 'deleted'
    /** Not in the local database at all — nothing can ever push this. */
    | 'missing';

export interface PendingDoc extends PendingEntry {
    state: PendingDocState;
    /** The document's own timestamp, empty when it is gone. */
    updatedAt: string;
    /** Local revision — how the check below notices an edit made while it ran. */
    rev: string;
    /** The stored document, for the comparison below. Absent when it is gone. */
    local?: Record<string, unknown>;
    /**
     * The server having sent this document back rather than taking it — the
     * answer to "why is this still here" in the case nothing else can show.
     */
    rejected?: PushRejection;
}

export type QueueCheckOutcome =
    /** The sync ran to the end; stale entries (if any) were dropped. */
    | 'checked'
    /** Only the entries with no document at all could be judged. */
    | 'partial'
    /** Nothing could be judged: no connection, another window syncs, or a
     *  colleague's queue is holding the push back. */
    | 'unavailable';

export interface QueueCheckResult {
    outcome: QueueCheckOutcome;
    removed: number;
    /** Entries still waiting afterwards — genuinely, as far as this could tell. */
    remaining: number;
    /**
     * True when the browser refused to store the journal, which makes every
     * other number here a wish rather than a result.
     */
    frozen: boolean;
}

/**
 * How long to give the sync before saying it did not finish.
 *
 * A device on a thin line can take a while to drain a real queue, and there is
 * no harm in waiting: nothing is dropped until the sync says it is idle.
 */
const IN_SYNC_TIMEOUT_MS = 20_000;

function key(entry: { collection: string; docId: string }): string {
    return `${entry.collection}/${entry.docId}`;
}

/**
 * The queued entries, each with the state of the document it names.
 *
 * Reads the storage instance rather than the collection, because deleted
 * documents have to be included: a tombstone still has to go up, but `findByIds`
 * hides it, and the entry would then be pruned as stale while it was in fact the
 * one thing left to send.
 */
export async function readPendingQueue(db: AppDatabase): Promise<PendingDoc[]> {
    const entries = pendingEntries();
    const byCollection = new Map<string, PendingEntry[]>();
    for (const entry of entries) {
        const group = byCollection.get(entry.collection);
        if (group) {
            group.push(entry);
        } else {
            byCollection.set(entry.collection, [entry]);
        }
    }

    const collections = db.collections as unknown as Record<
        string,
        RxCollection | undefined
    >;
    const docs: PendingDoc[] = [];
    for (const [name, group] of byCollection) {
        const found = await findLocalDocs(
            collections[name],
            group.map((entry) => entry.docId)
        );
        for (const entry of group) {
            const doc = found.get(entry.docId);
            docs.push({
                ...entry,
                state: !doc ? 'missing' : doc._deleted ? 'deleted' : 'waiting',
                updatedAt: String(doc?.updatedAt ?? ''),
                rev: String(doc?._rev ?? ''),
                local: doc,
                rejected: rejectionFor(entry.collection, entry.docId)
            });
        }
    }

    return docs.sort(
        (left, right) =>
            left.collection.localeCompare(right.collection) ||
            left.docId.localeCompare(right.docId)
    );
}

/** The documents behind a group of ids, tombstones included. */
async function findLocalDocs(
    collection: RxCollection | undefined,
    ids: string[]
): Promise<Map<string, RxDocumentData<Record<string, unknown>>>> {
    const found = new Map<string, RxDocumentData<Record<string, unknown>>>();
    if (!collection) {
        // A journal entry for a collection this build no longer has. It cannot
        // be pushed by anything, which is exactly what 'missing' says.
        return found;
    }
    const rows = (await collection.storageInstance.findDocumentsById(
        ids,
        true
    )) as RxDocumentData<Record<string, unknown>>[];
    for (const row of rows) {
        found.set(String(row[collection.schema.primaryPath as string]), row);
    }
    return found;
}

/**
 * Check the queue, and drop what is provably no longer waiting on anything. Two
 * things can be proven, and nothing else is touched:
 *
 * - an entry whose document is not in the local database has nothing left that
 *   could send it, whatever the connection is doing;
 * - once every replication reports itself in sync, an entry still in the journal
 *   on a document untouched since the check began is bookkeeping and nothing
 *   else.
 *
 * The second half needs this window to be the one that syncs (`awaitInSync`
 * never resolves in the others), a connection, and no colleague's queue holding
 * the push back — a held queue is a real "ausstehend".
 */
export async function checkPendingQueue(
    db: AppDatabase,
    online: boolean
): Promise<QueueCheckResult> {
    const before = await readPendingQueue(db);
    const stale = before.filter((doc) => doc.state === 'missing');
    let outcome: QueueCheckOutcome =
        stale.length > 0 ? 'partial' : 'unavailable';

    const canWait =
        online && isReplicatingHere() && getSyncBlockers().length === 0;
    if (canWait && (await awaitAllInSync())) {
        outcome = 'checked';
        // Read the journal again rather than trusting the snapshot: the sync we
        // just waited for may have cleared entries itself, and someone may have
        // gone on working while it ran. Only entries that are still there, on a
        // document that has not moved, are stale.
        const after = new Map(
            (await readPendingQueue(db)).map((doc) => [key(doc), doc])
        );
        stale.length = 0;
        for (const doc of before) {
            const now = after.get(key(doc));
            if (now && now.rev === doc.rev) {
                stale.push(doc);
            }
        }
    }

    /**
     * Before striking anything off: try to SEND it. An entry with no document
     * behind it is not unsendable — every local write leaves a copy in the
     * outbox (`db/outbox`), and the case that produces these entries is exactly
     * where that copy matters: a device wiped offline after its database broke,
     * leaving RxDB nothing to push.
     *
     * Only what is SETTLED is forgotten — sent, superseded, or with no copy
     * left. Anything the connection refused stays for the next quiet moment.
     */
    /**
     * Each entry with what the local database holds for it, so the copy is
     * only sent when it says something newer. A document that is merely stuck
     * in the bookkeeping costs no round trip, and an offline edit whose record
     * was pulled back from the server after a reset — local document present,
     * and older — is still rescued.
     */
    const settled = await uploadOrphans(
        stale.map((doc) => ({
            collection: doc.collection,
            docId: doc.docId,
            userId: doc.userId,
            localUpdatedAt: doc.updatedAt
        }))
    );
    const forgettable = stale.filter((doc) =>
        settled.has(`${doc.collection}/${doc.docId}`)
    );

    const stored = forgetPending(forgettable);
    if (stored) {
        // The note explained the entry; with the entry gone it is only clutter.
        forgetRejections(forgettable);
    }
    return {
        outcome,
        removed: stored ? forgettable.length : 0,
        remaining: (await readPendingQueue(db)).length,
        frozen: !stored
    };
}

/** Every replication idle at once, or `false` if that took too long. */
async function awaitAllInSync(): Promise<boolean> {
    if (replicationStates.length === 0) {
        return false;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), IN_SYNC_TIMEOUT_MS);
    });
    try {
        return await Promise.race([
            Promise.all(
                replicationStates.map((replication) =>
                    replication.awaitInSync()
                )
            ).then(() => true),
            timedOut
        ]);
    } catch {
        // A replication that errored out has not sent everything it had, so
        // nothing may be concluded from it.
        return false;
    } finally {
        clearTimeout(timer);
    }
}

// --- Doing it without being asked ---
/**
 *
 * A count that only a support call can unstick is not fixed, it is diagnosable.
 * The proof above needs no user: the window that syncs runs it whenever the
 * queue is not empty and the sync has gone quiet, which is exactly when the
 * answer is trustworthy. The dialog's button stays, but nothing depends on it.
 */

/** How long the queue has to sit still before it is worth looking at. */
const QUIET_MS = 5_000;

/** Never twice inside this, however often the journal twitches. */
const MIN_GAP_MS = 60_000;

let healTimer: ReturnType<typeof setTimeout> | null = null;
let healing = false;
let lastHealAt = 0;

/**
 * Sweep the queue clean in the background, in the window that does the syncing.
 * Returns the stop function; started once per session next to the replication
 * (see db/RxDBProvider). A window that is not replicating schedules nothing, and
 * picks it up if it later becomes the one that does.
 */
export function startQueueSelfHeal(db: AppDatabase): () => void {
    const schedule = () => {
        if (healTimer || healing || pendingEntries().length === 0) {
            return;
        }
        healTimer = setTimeout(() => {
            healTimer = null;
            void heal(db);
        }, QUIET_MS);
    };

    const unsubscribe = [
        subscribeAuthorJournal(schedule),
        subscribeReplicationPresence(schedule)
    ];
    schedule();

    return () => {
        for (const stop of unsubscribe) {
            stop();
        }
        if (healTimer) {
            clearTimeout(healTimer);
            healTimer = null;
        }
    };
}

async function heal(db: AppDatabase): Promise<void> {
    const now = Date.now();
    if (healing || now - lastHealAt < MIN_GAP_MS) {
        return;
    }
    // Offline, or another window is the one syncing: the check would conclude
    // nothing and the wait would be for nothing. It comes back when that
    // changes — both are things `schedule` is subscribed to.
    if (!isReplicatingHere() || !isOnline()) {
        return;
    }
    healing = true;
    try {
        await checkPendingQueue(db, true);
    } catch {
        // Whatever went wrong, the entries are still there and the next quiet
        // moment gets another go. Nothing here may throw into a subscriber.
    } finally {
        healing = false;
        lastHealAt = Date.now();
    }
}

function isOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
}

// --- Asking the server what it actually has ---
/**
 *
 * Everything above works out whether an entry can still be sent, not WHY it is
 * there. Three answers, each pointing somewhere different:
 *
 * - the server has exactly this state: the push DID go through and only the
 *   bookkeeping was left behind;
 * - the server has a DIFFERENT state: this device's change never arrived and
 *   RxDB believes it is in sync — the serious one;
 * - the server does not know the record: created here, never landed.
 *
 * Read-only, one GET per entry, never automatic.
 */

export type ServerComparison =
    | 'same'
    | 'different'
    | 'absent'
    /** No answer: offline, an error, or nothing local to compare against. */
    | 'unknown';

export interface PendingComparison {
    result: ServerComparison;
    /** Which content fields differ, when they do — the useful part of a report. */
    differing: string[];
}

const entityByCollection = new Map(
    entityConfigs.map((entity) => [entity.collection, entity])
);

export async function compareWithServer(
    docs: readonly PendingDoc[]
): Promise<Map<string, PendingComparison>> {
    const results = new Map<string, PendingComparison>();
    for (const doc of docs) {
        results.set(key(doc), await compareOne(doc));
    }
    return results;
}

async function compareOne(doc: PendingDoc): Promise<PendingComparison> {
    const entity = entityByCollection.get(doc.collection);
    if (!entity || !doc.local) {
        return { result: 'unknown', differing: [] };
    }
    let response: Response;
    try {
        response = await authFetch(
            `${entity.apiBase}/${encodeURIComponent(doc.docId)}`
        );
    } catch {
        return { result: 'unknown', differing: [] };
    }
    if (response.status === 404) {
        // A deletion that has landed reads exactly like this, and it is not a
        // finding — it is the deletion having worked.
        return {
            result: doc.state === 'deleted' ? 'same' : 'absent',
            differing: []
        };
    }
    if (!response.ok) {
        return { result: 'unknown', differing: [] };
    }

    const remote = (await response.json().catch(() => null)) as Record<
        string,
        unknown
    > | null;
    if (typeof remote !== 'object' || remote === null) {
        // Something answered that is not the record. Comparing against it would
        // report every field as differing, which reads like a real finding.
        return { result: 'unknown', differing: [] };
    }
    // `updatedAt` is stamped by the server on every accepted push and is
    // deliberately not part of the comparison — it never matches, and saying so
    // for every row would drown the fields that actually differ.
    const differing = entity.contentFields.filter(
        (field) =>
            field !== 'updatedAt' &&
            !valuesEqual(doc.local?.[field], remote[field])
    );
    return {
        result: differing.length === 0 ? 'same' : 'different',
        differing
    };
}
